import { Sparkles } from "lucide-react";

const TEMPLATES = [
  {
    id: "digital-agency",
    name: "Digital Agency",
    tagline: "Clean · purple · agency-grade",
    bg: "linear-gradient(180deg, #FAFAFB 0%, #FFFFFF 60%)",
    accent: "#6128DB",
    headline: "Scale your agency without the chaos.",
  },
  {
    id: "coach",
    name: "Coach & Educator",
    tagline: "Fresh · teal · approachable",
    bg: "linear-gradient(180deg, #F0FDF8 0%, #FFFFFF 60%)",
    accent: "#059669",
    headline: "Turn your coaching into a system.",
  },
  {
    id: "real-estate",
    name: "Real Estate",
    tagline: "Editorial · black · luxury",
    bg: "linear-gradient(180deg, #FAFAFA 0%, #F4F4F5 100%)",
    accent: "#18181B",
    headline: "Listings that feel like a magazine.",
  },
  {
    id: "health",
    name: "Health & Wellness",
    tagline: "Warm · amber · inviting",
    bg: "linear-gradient(180deg, #FFFBEB 0%, #FFFFFF 60%)",
    accent: "#B45309",
    headline: "The 30-day reset that actually sticks.",
  },
  {
    id: "saas",
    name: "SaaS & Product",
    tagline: "Bold · orange · modern",
    bg: "linear-gradient(180deg, #FFF7ED 0%, #FFFFFF 60%)",
    accent: "#F97316",
    headline: "Ship the launch page in an afternoon.",
  },
];

export function Showcase() {
  return (
    <section className="bg-white px-6 py-20 sm:py-28">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
          <div className="max-w-xl">
            <p
              data-reveal
              className="text-[11px] font-semibold uppercase tracking-[0.18em] text-violet-600"
            >
              Five design templates · more every week
            </p>
            <h2
              data-reveal
              className="mt-4 font-heading text-[32px] font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-[44px]"
            >
              Tuned for the niches you{" "}
              <span className="text-gradient-brand">actually sell to.</span>
            </h2>
          </div>
          <p
            data-reveal
            className="max-w-sm text-[14px] leading-relaxed text-slate-600"
          >
            Each template ships with its own type, color, and spacing system.
            Pick one, and every section variant inherits it cleanly.
          </p>
        </div>

        <div
          data-reveal-stagger
          className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          {TEMPLATES.map((t) => (
            <div
              key={t.id}
              className="group relative flex flex-col overflow-hidden rounded-2xl border border-slate-200/80 ring-1 ring-slate-900/[0.02] transition-all hover:-translate-y-1 hover:shadow-[0_22px_40px_-22px_rgba(15,23,42,0.25)]"
              style={{ background: t.bg }}
            >
              <div className="flex-1 p-6 sm:p-7">
                <div
                  className="mb-3 inline-flex h-2 w-2 rounded-full"
                  style={{ background: t.accent }}
                />
                <p
                  className="font-heading text-[19px] font-bold leading-[1.15] tracking-tight"
                  style={{ color: t.accent }}
                >
                  {t.headline}
                </p>
                <div className="mt-4 space-y-1.5">
                  <div className="h-1.5 w-3/4 rounded-full bg-slate-200/70" />
                  <div className="h-1.5 w-2/3 rounded-full bg-slate-200/70" />
                  <div className="h-1.5 w-1/2 rounded-full bg-slate-200/70" />
                </div>
                <div
                  className="mt-5 inline-block rounded-md px-2.5 py-1.5 text-[9px] font-semibold uppercase tracking-wider text-white"
                  style={{ background: t.accent }}
                >
                  Get started
                </div>
              </div>
              <div className="border-t border-slate-200/60 bg-white/70 px-6 py-4 backdrop-blur sm:px-7">
                <p className="font-heading text-[14px] font-semibold tracking-tight text-slate-900">
                  {t.name}
                </p>
                <p className="text-[11px] text-slate-500">{t.tagline}</p>
              </div>
            </div>
          ))}

          {/* 6th — "more coming" card */}
          <div className="group relative flex flex-col overflow-hidden rounded-2xl border border-dashed border-slate-300 bg-gradient-to-br from-slate-50 via-white to-violet-50/40 ring-1 ring-slate-900/[0.02]">
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-7 text-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100 ring-1 ring-violet-200">
                <Sparkles className="h-4 w-4 text-violet-600" />
              </div>
              <div className="font-heading text-[19px] font-bold leading-[1.15] tracking-tight text-slate-900">
                More added every week.
              </div>
              <p className="max-w-[220px] text-[12.5px] leading-relaxed text-slate-500">
                Voted by Lifetime members. New niche templates ship Fridays —
                yours forever.
              </p>
              <div className="mt-1 flex items-center gap-1">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className="h-1.5 w-1.5 animate-pulse rounded-full bg-violet-500"
                    style={{ animationDelay: `${i * 200}ms` }}
                  />
                ))}
              </div>
            </div>
            <div className="border-t border-slate-200/60 bg-white/70 px-6 py-4 backdrop-blur sm:px-7">
              <p className="font-heading text-[14px] font-semibold tracking-tight text-slate-900">
                Coming soon
              </p>
              <p className="text-[11px] text-slate-500">
                E-commerce · podcast · membership · creator
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
